/**
 * 历史轨迹图层渲染 / 地图资源清理（从 Dashboard 拆出）
 */

import { toMapLngLat } from './basemapCoord';
import { notifyMemberMarkersProjectionReady } from './updateMemberMarkersOnMap';
import mapboxgl from 'mapbox-gl';
import { isBogusGpsCoordinate } from '../bogusGps';
import { sanitizeTrackPoints, splitTrackSegments } from './trackSegmentHelpers';
import { resolveTrackColor } from './trackColorHelpers';

/** Mapbox source/layer id（与 trackLineClickHelpers 解析约定一致：track-{name}） */
export function historyTrackLayerId(memberName) {
  return `track-${String(memberName || 'unknown')}`;
}

/** 轨迹点原始 WGS84（未纠偏） */
export function readTrackPointWgs(point) {
  if (!point) return null;
  const lng = Number(point.longitude ?? point.lng ?? point.lon ?? point.last_longitude);
  const lat = Number(point.latitude ?? point.lat ?? point.last_latitude);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return { lng, lat };
}

/** 轨迹是否含有可用于飞视野的点（排除 MaxMind 美国占位等） */
export function trackHasUsableCoordinates(points = []) {
  return (points || []).some((p) => {
    const wgs = readTrackPointWgs(p);
    if (!wgs) return false;
    return !isBogusGpsCoordinate(wgs.lat, wgs.lng);
  });
}

export function buildHistoryTrackCoordinates(points = [], map = null) {
  return (points || [])
    .map((p) => {
      const wgs = readTrackPointWgs(p);
      if (!wgs) return null;
      // 伪定位点不上图，避免「虚拟机1」在美国画出轨迹并把相机拽过去
      if (isBogusGpsCoordinate(wgs.lat, wgs.lng)) return null;
      const coord = toMapLngLat(wgs.lng, wgs.lat, undefined, map);
      if (!coord || Number.isNaN(coord[0]) || Number.isNaN(coord[1])) return null;
      return coord;
    })
    .filter(Boolean);
}

/** 单点时复制微偏移，保证至少能画出短线 */
export function ensureLineCoordinates(coordinates = []) {
  if (!coordinates || coordinates.length === 0) return [];
  if (coordinates.length === 1) {
    const [lng, lat] = coordinates[0];
    return [
      [lng, lat],
      [lng + 0.00005, lat]
    ];
  }
  return coordinates;
}

export function isMapStyleMutable(map) {
  if (!map) return false;
  try {
    if (typeof map.getStyle !== 'function' || !map.getStyle()) return false;
  } catch (_) {
    return false;
  }
  try {
    // 切底图过渡期 addSource 会抛错；已有历史轨迹 source 时仍允许 setData
    if (typeof map.isStyleLoaded === 'function' && !map.isStyleLoaded()) {
      return false;
    }
  } catch (_) {
    return false;
  }
  return true;
}

export function removeHistoryTrackLayers(map, trackDataMap = {}) {
  if (!map) return;
  Object.keys(trackDataMap || {}).forEach((name) => {
    const id = historyTrackLayerId(name);
    try {
      if (map.getLayer?.(`${id}-hit`)) map.removeLayer(`${id}-hit`);
      if (map.getLayer?.(id)) map.removeLayer(id);
      if (map.getSource?.(id)) map.removeSource(id);
    } catch (error) {
      console.warn(`清理轨迹资源失败: ${name}`, error);
    }
  });
}

/**
 * 将视野飞到历史轨迹范围（退出地球自转后由调用方先 stop）
 * @returns {boolean}
 */
export function fitMapToHistoryTrack(map, points = [], options = {}) {
  if (!map || !points?.length) return false;
  if (!trackHasUsableCoordinates(points)) {
    console.warn('轨迹全为伪定位点，跳过飞视野');
    return false;
  }
  const cleaned = sanitizeTrackPoints(points);
  const coordinates = ensureLineCoordinates(
    buildHistoryTrackCoordinates(cleaned.length ? cleaned : points, map)
  );
  if (coordinates.length < 1) return false;

  try {
    if (typeof map.setProjection === 'function') {
      const proj = map.getProjection?.();
      if (proj && String(proj.name || proj).toLowerCase() === 'globe') {
        map.setProjection('mercator');
      }
    }
    // HTML Marker 在 globe→mercator 后需显式唤醒；否则开轨迹飞视野后图标仍缺
    notifyMemberMarkersProjectionReady(map);
  } catch (_) {
    /* ignore */
  }

  if (coordinates.length === 1) {
    map.flyTo({
      center: coordinates[0],
      zoom: Math.max(map.getZoom?.() || 0, 14),
      essential: true,
      ...(options.flyTo || {})
    });
    return true;
  }

  const bounds = coordinates.reduce(
    (b, c) => b.extend(c),
    new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
  );
  map.fitBounds(bounds, {
    padding: options.padding ?? 80,
    maxZoom: options.maxZoom ?? 16,
    duration: options.duration ?? 900,
    essential: true
  });
  return true;
}

/**
 * 按 showTracks 渲染历史轨迹线
 * @returns {{ deferred: boolean, rendered: number, skippedEmpty: number }}
 */
export function renderHistoryTracksOnMap(map, options = {}) {
  if (!map) return { deferred: false, rendered: 0, skippedEmpty: 0 };

  const {
    trackData = {},
    showTracks = {},
    trackColors = {},
    members = []
  } = options;

  if (!isMapStyleMutable(map)) {
    console.warn('历史轨迹渲染延期：地图样式尚未就绪');
    return { deferred: true, rendered: 0, skippedEmpty: 0 };
  }

  console.log('开始渲染轨迹，当前状态:', {
    trackDataKeys: Object.keys(trackData),
    showTracks,
    trackDataSizes: Object.fromEntries(
      Object.entries(trackData).map(([name, data]) => [name, data ? data.length : 0])
    )
  });

  // 先清掉不再显示的
  Object.keys(trackData || {}).forEach((name) => {
    if (showTracks[name]) return;
    const id = historyTrackLayerId(name);
    try {
      if (map.getLayer?.(`${id}-hit`)) map.removeLayer(`${id}-hit`);
      if (map.getLayer?.(id)) map.removeLayer(id);
      if (map.getSource?.(id)) map.removeSource(id);
    } catch (_) {
      /* ignore */
    }
  });

  let rendered = 0;
  let skippedEmpty = 0;

  Object.entries(showTracks).forEach(([name, visible]) => {
    if (!visible) return;
    const points = trackData[name];
    if (!points || points.length < 1) {
      skippedEmpty += 1;
      return;
    }

    const segments = splitTrackSegments(points);
    let lineCoords = segments
      .map((seg) => buildHistoryTrackCoordinates(seg, map))
      .filter((coords) => coords.length >= 2);
    // 真·单点轨迹才补短线；瞬移切段留下的孤立点不要画成 5 米短线
    if (!lineCoords.length) {
      const padded = ensureLineCoordinates(buildHistoryTrackCoordinates(points, map));
      if (padded.length >= 2) lineCoords = [padded];
    }
    if (!lineCoords.length) {
      console.warn(`轨迹点不足或坐标无效: ${name}`);
      skippedEmpty += 1;
      return;
    }

    console.log(`渲染轨迹: ${name}`, points.length, '个点 →', lineCoords.length, '段');
    const id = historyTrackLayerId(name);
    const geojson = lineCoords.length === 1
      ? { type: 'Feature', geometry: { type: 'LineString', coordinates: lineCoords[0] } }
      : { type: 'Feature', geometry: { type: 'MultiLineString', coordinates: lineCoords } };
    const color = resolveTrackColor(name, trackColors, { name, members });

    try {
      const existing = map.getSource?.(id);
      if (existing && typeof existing.setData === 'function') {
        existing.setData(geojson);
        if (map.getLayer?.(id)) {
          try { map.setPaintProperty(id, 'line-color', color); } catch (_) { /* ignore */ }
        } else {
          map.addLayer({
            id,
            type: 'line',
            source: id,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': color,
              'line-width': 4,
              'line-opacity': 0.8
            }
          });
        }
        if (!map.getLayer?.(`${id}-hit`)) {
          map.addLayer({
            id: `${id}-hit`,
            type: 'line',
            source: id,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': '#000000',
              'line-width': 14,
              'line-opacity': 0
            }
          });
        }
        rendered += 1;
        return;
      }

      // 残留旧图层时先清
      try {
        if (map.getLayer?.(`${id}-hit`)) map.removeLayer(`${id}-hit`);
        if (map.getLayer?.(id)) map.removeLayer(id);
        if (map.getSource?.(id)) map.removeSource(id);
      } catch (_) {
        /* ignore */
      }

      map.addSource(id, { type: 'geojson', data: geojson });
      map.addLayer({
        id,
        type: 'line',
        source: id,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': color,
          'line-width': 4,
          'line-opacity': 0.8
        }
      });
      map.addLayer({
        id: `${id}-hit`,
        type: 'line',
        source: id,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#000000',
          'line-width': 14,
          'line-opacity': 0
        }
      });
      console.log(`轨迹图层添加成功: ${name}`);
      rendered += 1;
    } catch (error) {
      console.error(`添加轨迹图层失败: ${name}`, error);
    }
  });

  // 轨迹画上后若已是 mercator，唤醒可能被推迟的 HTML 队员图标（不强制切投影，避免地球概览误退出）
  if (rendered > 0) {
    try {
      const proj = map.getProjection?.();
      const name = proj && String(proj.name || proj).toLowerCase();
      if (name !== 'globe') {
        notifyMemberMarkersProjectionReady(map);
      }
    } catch (_) {
      /* ignore */
    }
  }

  return { deferred: false, rendered, skippedEmpty };
}

export function removeMemberMarkerLayers(map, members = []) {
  if (!map) return;
  members.forEach((member) => {
    try {
      const markerId = `marker-${member.device_unique_id}`;
      if (map.getLayer?.(markerId)) map.removeLayer(markerId);
      if (map.getSource?.(markerId)) map.removeSource(markerId);
    } catch (error) {
      console.warn(`清理标记资源失败: ${member.device_unique_id}`, error);
    }
  });
}

/**
 * 清理轨迹/标记图层并销毁地图实例
 */
export function safelyDestroyMap(map, options = {}) {
  if (!map) return null;
  const { trackData = {}, members = [] } = options;
  try {
    removeHistoryTrackLayers(map, trackData);
    removeMemberMarkerLayers(map, members);
    map.remove();
    console.log('地图资源清理完成');
  } catch (error) {
    console.error('清理地图资源时出错:', error);
  }
  return null;
}
